import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withCtx as _withCtx, TransitionGroup as _TransitionGroup } from "vue"

const _hoisted_1 = { class: "drag-preview-name" }
const _hoisted_2 = {
  key: 1,
  class: "drag-preview-badge"
}
const _hoisted_3 = ["aria-label", "aria-rowcount", "aria-colcount", "aria-activedescendant"]
const _hoisted_4 = {
  key: 0,
  class: "header-group"
}
const _hoisted_5 = ["aria-sort", "onClick", "onKeydown"]
const _hoisted_6 = ["id", "aria-selected", "aria-label", "aria-rowindex", "draggable", "onMouseenter", "onDragstart", "onDragover", "onDrop", "onClick", "onDblclick"]
const _hoisted_7 = {
  key: 0,
  class: "visually-hidden"
}
const _hoisted_8 = {
  class: "column actions",
  role: "gridcell"
}

import { ref, toRefs, watch, onUnmounted, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDirectoryGrid, type GridOptions } from './useDirectoryGrid'
import { useMapboxFeatureState } from '@/pinia/mapboxFeatureState'
import type { Action, DirectoryItem, DirectoryItemType, Header } from '../types'
import ChevronToggle from '@/design-system/atoms/ChevronToggle.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import ActionMenu from '../ActionMenu.vue'
import ActionButton from '../ActionButton.vue'
import IconFolderSmall from '@/design-system/assets/icons/IconFolderSmall.vue'
import IconFileSmall from '@/design-system/assets/icons/IconFileSmall.vue'
import IconLinkSmall from '@/design-system/assets/icons/IconLinkSmall.vue'
import DirectoryItemField from '../DirectoryItemField.vue'

type Props = {
  headers?: Header[]
  showHeaders?: boolean
  directoryItems: DirectoryItem[]
  actions?: Action[]
  maxActions?: number
  ariaLabel?: string
  currentItemId?: string
  dragDropEnabled?: boolean
  multiSelectEnabled?: boolean
  disabledTypes?: DirectoryItemType[]
  showFooter?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DirectoryListView',
  props: {
    headers: { default: () => [{ key: 'name', translationKey: 'name', labelKey: 'general__text__name' }] },
    showHeaders: { type: Boolean, default: false },
    directoryItems: {},
    actions: { default: () => [] },
    maxActions: { default: 3 },
    ariaLabel: { default: 'Directory content' },
    currentItemId: { default: undefined },
    dragDropEnabled: { type: Boolean },
    multiSelectEnabled: { type: Boolean },
    disabledTypes: { default: () => [] },
    showFooter: { type: Boolean, default: false }
  },
  emits: ["select", "doubleClick", "action", "sort", "itemsDroppedOnTarget", "loadMoreItems"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const { actions } = toRefs(props)

const emit = __emit

const { t } = useI18n()
const { resetRecords, setRecords } = useMapboxFeatureState()

const gridComponentRootRef = ref<HTMLElement>()
const gridBodyRef = ref<HTMLElement>()
const gridTableRef = ref<HTMLTableElement>()
const customDragImageRef = ref<HTMLElement>()

const gridOptions: GridOptions = {
  getGridBodyHeight: () => gridBodyRef.value?.clientHeight ?? 0,
  isGridFocused: () => gridComponentRootRef.value?.contains(document.activeElement) ?? false,
  scrollToRow: (id: string) => {
    document.getElementById(id)?.scrollIntoView({ block: 'nearest' })
  },
  setDragImage: (dataTransfer: DataTransfer) => {
    if (customDragImageRef.value) {
      dataTransfer.setDragImage(customDragImageRef.value, 20, 20)
    }
  },
  focusGrid: () => {
    const tableEl = gridComponentRootRef.value?.querySelector('table')
    tableEl?.focus()
  }
}

const grid = useDirectoryGrid(toRefs(props), emit, gridOptions)

function checkItemFilling() {
  if (gridComponentRootRef.value && gridTableRef.value) {
    const rootSize = gridComponentRootRef.value.getBoundingClientRect()
    const tableSize = gridTableRef.value.getBoundingClientRect()
    const tableLargerThanRoot = tableSize.height > rootSize.height
    if (!tableLargerThanRoot) {
      emit('loadMoreItems')
    }
  }
}

function checkScrollBottom() {
  if (gridComponentRootRef.value) {
    const scrollTop = gridComponentRootRef.value.scrollTop
    const scrollHeight = gridComponentRootRef.value.scrollHeight
    const clientHeight = gridComponentRootRef.value.clientHeight
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1 // Allow a small buffer
    if (atBottom) {
      emit('loadMoreItems')
    }
  }
}

onMounted(() => {
  checkItemFilling()
  if (gridComponentRootRef.value) {
    new ResizeObserver(() => {
      checkItemFilling()
    }).observe(gridComponentRootRef.value)
    gridComponentRootRef.value.addEventListener('scroll', checkScrollBottom)
  }
  if (gridTableRef.value) {
    new ResizeObserver(() => {
      checkItemFilling()
    }).observe(gridTableRef.value)
  }
})

onUnmounted(() => {
  resetRecords()
})

watch(
  () => [grid.selectedItemIds, grid.hoveredItem] as const,
  ([newSelections, newHoveredItem]) => {
    const records = (newSelections ?? []).reduce((acc, id) => {
      const newItem = props.directoryItems.find((item) => item.id === id)
      return {
        ...acc,
        ...(newItem?.entityConnectionPath ?? []).reduce((innerAcc, pathId) => {
          innerAcc[pathId] = { color: '#fadb14' }
          return innerAcc
        }, {} as Record<string, { color: string }>)
      }
    }, {})
    const hoveredItemRecords = (newHoveredItem?.entityConnectionPath ?? []).reduce((acc, id) => {
      acc[id] = { color: '#fce94d' }
      return acc
    }, {} as Record<string, { color: string }>)
    setRecords({
      ...records,
      ...hoveredItemRecords
    })
  },
  { deep: true, immediate: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "gridComponentRootRef",
    ref: gridComponentRootRef,
    class: "directory-list-view",
    tabindex: "-1",
    onClick: _cache[5] || (_cache[5] = _withModifiers(
//@ts-ignore
(...args) => (_unref(grid).handleClickOnContainer && _unref(grid).handleClickOnContainer(...args)), ["self"])),
    onFocusin: _cache[6] || (_cache[6] = 
//@ts-ignore
(...args) => (_unref(grid).handleComponentFocusIn && _unref(grid).handleComponentFocusIn(...args))),
    onFocusout: _cache[7] || (_cache[7] = 
//@ts-ignore
(...args) => (_unref(grid).handleComponentFocusOut && _unref(grid).handleComponentFocusOut(...args))),
    onKeydown: _cache[8] || (_cache[8] = 
//@ts-ignore
(...args) => (_unref(grid).handleKeyDown && _unref(grid).handleKeyDown(...args)))
  }, [
    (_unref(grid).isDragging)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref_key: "customDragImageRef",
          ref: customDragImageRef,
          class: "custom-drag-preview"
        }, [
          (_unref(grid).dragPreviewIconComponent)
            ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(grid).dragPreviewIconComponent), {
                key: 0,
                class: "drag-preview-icon",
                "aria-hidden": "true"
              }))
            : _createCommentVNode("", true),
          _createElementVNode("span", _hoisted_1, _toDisplayString(_unref(grid).dragPreviewName), 1),
          (_unref(grid).dragPreviewCount > 1)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(_unref(grid).dragPreviewCount), 1))
            : _createCommentVNode("", true)
        ], 512))
      : _createCommentVNode("", true),
    _createElementVNode("table", {
      ref_key: "gridTableRef",
      ref: gridTableRef,
      class: "grid-table",
      "aria-label": _ctx.ariaLabel || 'Directory content',
      "aria-rowcount": _ctx.directoryItems.length > 0 ? _ctx.directoryItems.length : -1,
      "aria-colcount": _unref(grid).localHeaders.length + 1,
      "aria-activedescendant": _unref(grid).activeRowId,
      onFocus: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(grid).onGridFocus && _unref(grid).onGridFocus(...args)))
    }, [
      (_ctx.showHeaders)
        ? (_openBlock(), _createElementBlock("thead", _hoisted_4, [
            _createElementVNode("tr", {
              class: "header-row",
              style: _normalizeStyle({ 'grid-template-columns': _unref(grid).columnsTemplate })
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(grid).localHeaders, (header) => {
                return (_openBlock(), _createElementBlock("th", {
                  key: header.key,
                  class: _normalizeClass([
              'column',
              'header',
              {
                'header--sortable': header.sortable !== false
              }
            ]),
                  scope: "col",
                  "aria-sort": _unref(grid).getAriaSort(header),
                  onClick: ($event: any) => (_unref(grid).handleSort(header)),
                  onKeydown: [
                    _withKeys(_withModifiers(($event: any) => (_unref(grid).handleSort(header)), ["prevent"]), ["enter"]),
                    _withKeys(_withModifiers(($event: any) => (_unref(grid).handleSort(header)), ["prevent"]), ["space"])
                  ]
                }, [
                  _createVNode(TextBase, {
                    type: "paragraph",
                    weight: "bold",
                    text: _unref(t)(`general__text__${header.translationKey}`)
                  }, null, 8, ["text"]),
                  (header.sortable !== false)
                    ? (_openBlock(), _createBlock(ChevronToggle, {
                        key: 0,
                        state: 
                header.sortDirection ? { asc: 'up' as const, desc: 'down' as const }[header.sortDirection] : undefined
              ,
                        class: "header-chevron",
                        "aria-hidden": "true"
                      }, null, 8, ["state"]))
                    : _createCommentVNode("", true)
                ], 42, _hoisted_5))
              }), 128)),
              _cache[9] || (_cache[9] = _createElementVNode("th", {
                class: "column header",
                scope: "col",
                "aria-label": "Actions"
              }, null, -1))
            ], 4)
          ]))
        : _createCommentVNode("", true),
      _createVNode(_TransitionGroup, {
        is: "tbody",
        ref_key: "gridBodyRef",
        ref: gridBodyRef,
        class: "grid-body",
        name: "list"
      }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.directoryItems, (item, index) => {
            return (_openBlock(), _createElementBlock("tr", {
              id: `row-${item.id}`,
              key: item.id,
              class: _normalizeClass(["data-row", {
            'row--selected': _unref(grid).selectedItemIds.includes(item.id),
            'row--focused':
              index === _unref(grid).focusedRowIndex && _unref(grid).gridHasFocus && _unref(grid).isKeyboardNavigating,
            'row--dragging-source':
              _unref(grid).isDragging && _unref(grid).currentlyDraggedItemIds.includes(item.id),
            'row--drop-target': _unref(grid).dropTargetItemId === item.id,
            'row--disabled': _ctx.disabledTypes.includes(item.type),
            'row--archived': item.archived
          }]),
              style: _normalizeStyle({ 'grid-template-columns': _unref(grid).columnsTemplate }),
              "aria-selected": _unref(grid).selectedItemIds.includes(item.id),
              "aria-label": item.name,
              "aria-rowindex": index + 1,
              draggable: _ctx.dragDropEnabled,
              onMouseenter: ($event: any) => (_unref(grid).handleMouseEnterRow(item)),
              onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (_unref(grid).handleMouseLeaveRow())),
              onDragstart: ($event: any) => (_unref(grid).handleDragStart(item, $event)),
              onDragend: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(grid).handleDragEnd && _unref(grid).handleDragEnd(...args))),
              onDragover: _withModifiers(($event: any) => (_unref(grid).handleDragOverItem(item, $event)), ["prevent"]),
              onDragleave: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(grid).handleDragLeaveItem && _unref(grid).handleDragLeaveItem(...args))),
              onDrop: _withModifiers(($event: any) => (_unref(grid).handleDropOnItem(item, $event)), ["prevent"]),
              onClick: ($event: any) => (_unref(grid).handleClickOnRow($event, item, index)),
              onDblclick: ($event: any) => (_unref(grid).handleDoubleClick(item))
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(grid).localHeaders, (header, cellIndex) => {
                return (_openBlock(), _createElementBlock("td", {
                  key: header.key,
                  class: "column",
                  role: "gridcell"
                }, [
                  (cellIndex === 0 && item.type)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(item.type), 1))
                    : _createCommentVNode("", true),
                  (cellIndex === 0 && item.type === 'directory')
                    ? (_openBlock(), _createBlock(IconFolderSmall, {
                        key: 1,
                        class: "row-icon",
                        "aria-hidden": "true"
                      }))
                    : (cellIndex === 0 && item.type === 'file')
                      ? (_openBlock(), _createBlock(IconFileSmall, {
                          key: 2,
                          class: "row-icon",
                          "aria-hidden": "true"
                        }))
                      : (cellIndex === 0 && item.type === 'link')
                        ? (_openBlock(), _createBlock(IconLinkSmall, {
                            key: 3,
                            src: "Icon-Link-Small",
                            class: "row-icon",
                            "aria-hidden": "true"
                          }))
                        : _createCommentVNode("", true),
                  _createVNode(DirectoryItemField, {
                    class: "field",
                    item: item,
                    field: header.key,
                    options: { type: 'paragraph' }
                  }, null, 8, ["item", "field"])
                ]))
              }), 128)),
              _createElementVNode("td", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(actions).slice(0, _ctx.maxActions + 1), (action) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: action.actionName
                  }, [
                    (action.appliesOn?.includes(item.type) ?? true)
                      ? (_openBlock(), _createBlock(ActionButton, {
                          key: 0,
                          class: "row-action-button",
                          invert: _unref(grid).selectedItemIds.includes(item.id),
                          action: action,
                          tabIndex: index === _unref(grid).focusedRowIndex && _unref(grid).gridHasFocus ? 0 : -1,
                          "stop-click-propagation": "",
                          onAction: (action) => _ctx.$emit('action', action.actionName, [item])
                        }, null, 8, ["invert", "action", "tabIndex", "onAction"]))
                      : _createCommentVNode("", true)
                  ], 64))
                }), 128)),
                (_unref(actions).length - 1 > _ctx.maxActions)
                  ? (_openBlock(), _createBlock(ActionMenu, {
                      key: 0,
                      item: item,
                      actions: _unref(actions).slice(_ctx.maxActions + 1),
                      invert: _unref(grid).selectedItemIds.includes(item.id),
                      tabIndex: index === _unref(grid).focusedRowIndex && _unref(grid).gridHasFocus ? 0 : -1,
                      "stop-click-propagation": "",
                      onClose: _unref(grid).handleCloseMenu,
                      onAction: _cache[0] || (_cache[0] = (act, it) => _ctx.$emit('action', act.actionName, it ? [it] : []))
                    }, {
                      button: _withCtx(() => [
                        (_unref(actions).length)
                          ? (_openBlock(), _createBlock(ActionButton, {
                              key: 0,
                              action: {
                    actionName: 'more',
                    appliesOn: [],
                    icon: 'IconMoreVertical',
                    labelKey: 'general__text__more-actions'
                  },
                              invert: _unref(grid).selectedItemIds.includes(item.id),
                              tabIndex: index === _unref(grid).focusedRowIndex && _unref(grid).gridHasFocus ? 0 : -1
                            }, null, 8, ["invert", "tabIndex"]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 2
                    }, 1032, ["item", "actions", "invert", "tabIndex", "onClose"]))
                  : _createCommentVNode("", true)
              ])
            ], 46, _hoisted_6))
          }), 128))
        ]),
        _: 1
      }, 512)
    ], 40, _hoisted_3)
  ], 544))
}
}

})